// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on Expo route imports in this file; repo-native toolchain resolves them correctly
// @ts-nocheck
/**
 * Analysis Results Route - Dedicated URL for sharing and deep linking
 *
 * Features:
 * - Shareable direct links to analysis results
 * - Browser back button support
 * - Deep linking from mobile
 * - Preserves theatrical mode from URL parameter
 * - SEO-friendly URLs for public results
 *
 * URL Structure:
 * - /results/{analysisId}?mode=theatrical
 * - /results/{analysisId}?mode=quick
 * - /results/{analysisId}?mode=dramax_premium
 *
 * @see docs/THEATRICAL_MODE_IMPLEMENTATION_SUMMARY.md
 */

// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react normally
import React, { useEffect, useMemo } from "react";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react-native normally
import {
	View,
	ScrollView,
	StyleSheet,
	ActivityIndicator,
	Pressable,
} from "react-native";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves expo-router normally
import { useLocalSearchParams, useRouter } from "expo-router";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves expo-linear-gradient normally
import { LinearGradient } from "expo-linear-gradient";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react-native-reanimated normally
import Animated, { FadeIn, SlideInUp } from "react-native-reanimated";
// @ts-expect-error -- pi-lens Deno resolver false positive for workspace package resolution
import { TierBody } from "@dramadetector/ui";
import { useTranslation } from "@dramadetector/shared";
import { debugLog, criticalLog } from "../../../lib/debug-logger";

// Hooks
import { useAuth } from "../../../contexts/AuthContext";
import { useTier } from "../../../hooks/useTier";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { captureEvent } from "../../../lib/analytics";
import { scrubString } from "../../../lib/analytics/scrubbers";
import { getResultsPageViewSource } from "../../../lib/results/getResultsPageViewSource";
import { useResponsiveScreenPadding } from "../../../components/layout/TabScreenWrapper";

// Components
import { CrisisBanner } from "../../../components/safety/CrisisBanner";
import {
	ModernResultsPreview,
	ModernResultsFull,
} from "../../../components/modern-results";
import { DDGenerativeResults } from "../../../components/modern-results/DDGenerativeResults";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { OfflineBanner } from "../../../components/OfflineBanner";

// Feature flags
import {
	useL3AccordionsFlag,
	useGenerativeResultsFlag,
} from "../../../hooks/useFeatureFlags";

// Gating components
import { GatedSection } from "../../../components/gating";
import { DramaPlusPreview } from "../../../components/screens/conversion";

// Hook for fetching analysis result by ID
import { useAnalysisResult } from "../../../hooks/useAnalysisResult";
import { useResultsExperiencePolicy } from "../../../hooks/useResultsExperiencePolicy";

import type { QuotaTier } from "../../../components/trial/QuotaExhaustedModal";
import type { RevisionTrackingConfig } from "../../../components/modern-results/inline-annotations/InlineMessageDisplay";
import type { ResultsExperiencePolicyOutput } from "../../../../../packages/shared/src/types/screen-experience.ts";

export default function ResultsScreen() {
	void React;
	criticalLog("mount", "ResultsScreen mounted", {});

	// eslint-disable-next-line local-rules/no-translation-namespace-mismatch -- results screen intentionally reuses home-result copy keys
	const { t } = useTranslation("home");
	const router = useRouter();
	const { bottomPadding } = useResponsiveScreenPadding();
	const params = useLocalSearchParams<{
		analysisId: string;
		mode?: string;
		/** Shared link token for accessing analyses via public share URL */
		token?: string;
	}>();

	debugLog("mount", "ResultsScreen params", {
		analysisId: params.analysisId,
		mode: params.mode,
		hasToken: !!params.token,
	});

	// Auth and session state
	const {
		accessToken,
		user,
		isAnonymous,
		isRealUser,
		loading: authLoading,
	} = useAuth();

	// Tier level for tier-gated features
	const {
		tier: derivedTier,
		tierLevel,
		visualLayer,
		source,
		isPremium,
	} = useTier();
	const { tier, isLoading: isEntitlementsLoading } = useEntitlements();

	// Feature flags
	const l3AccordionsEnabled = useL3AccordionsFlag();
	const generativeResultsEnabled = useGenerativeResultsFlag();

	// Fetch analysis result (pass shared link token if present)
	const {
		data: analysis,
		isLoading,
		error,
	} = useAnalysisResult(params.analysisId, params.token);

	// Derive current tier — wait for entitlements before committing to a view.
	// Previous bug: defaulting to "free" during loading caused premium users to
	// see ModernResultsPreview ("Upgrade for More Insights") after theatrical
	// animation redirect, because entitlements hadn't resolved yet.
	// Phase 2 (NocoDB #3869 close-out): use canonical `isAnonymous` from AuthContext.
	const currentTier: QuotaTier = (() => {
		if (isAnonymous) return "anonymous";
		if (!tier) return "free"; // No entitlements data at all — true free user
		return tier as QuotaTier;
	})();

	// Map tier for components that don't support family_member
	const displayTier =
		currentTier === "family_member" ? "drama_plus" : currentTier;

	const resultsPolicy = useResultsExperiencePolicy({
		surface: "results",
		accessToken,
		userId: user?.id ?? null,
		isAnonymous,
		isRealUser,
		authLoading,
		tier: derivedTier,
		tierLevel,
		visualLayer,
		source,
		isPremium,
		isEntitlementsLoading,
		analysisCount: null,
		isTrialStateLoaded: true,
		analysis: analysis?.legacyAnalysis ?? null,
		hasRemoteConfig: generativeResultsEnabled,
		hasSharedLinkPayload: Boolean(params.token),
		errorCode: error?.message ?? null,
	}) as ResultsExperiencePolicyOutput;

	// Keep logging and page-view analytics in one effect so the results route
	// has a single async/side-effect orchestration point.
	useEffect(() => {
		debugLog("state", "ResultsScreen data state", {
			hasAnalysis: !!analysis,
			isLoading,
			hasError: !!error,
			analysisId: params.analysisId,
		});

		if (!analysis) {
			return;
		}

		// Compute TTL remaining % for tier persistence analytics (PostHog insight 6).
		// Only present on DB-fetched results (shared links, page refreshes) —
		// React Query cache hits omit ttlExpiresAt, so this naturally self-filters.
		const TTL_HOURS: Record<string, number> = {
			anonymous: 2,
			free: 24,
			drama_plus: 168,
			dramax: 168,
		};
		const ttlRemainingPercent = analysis.ttlExpiresAt
			? (() => {
					const totalHours = TTL_HOURS[currentTier] ?? 24;
					const hoursRemaining =
						(new Date(analysis.ttlExpiresAt).getTime() - Date.now()) /
						(1000 * 60 * 60);
					return Math.round(
						Math.max(0, Math.min(100, (hoursRemaining / totalHours) * 100)),
					);
				})()
			: undefined;

		captureEvent("results_page_view", {
			analysis_id: params.analysisId,
			theatrical_mode: params.mode || "default",
			source: getResultsPageViewSource(params),
			tier,
			ttl_remaining_percent: ttlRemainingPercent,
		});
	}, [
		analysis,
		isLoading,
		error,
		params.analysisId,
		params.mode,
		tier,
		currentTier,
	]);

	// Build revision tracking config from the NarrativeClarity envelope.
	const trackingConfig: RevisionTrackingConfig | undefined = analysis
		? {
				analysisId: analysis.id,
				originalScore: analysis.result?.score ?? 0,
				originalSummary: analysis.result?.summary ?? "",
				modelId:
					analysis.provenance.model || analysis.legacyAnalysis.metadata.model,
				enabled: !!accessToken,
				// M4.2: Scrub error_message to prevent PII leakage to PostHog
				onTrackingError: (error: Error, context: string) => {
					captureEvent("Revision_Tracking_Error", {
						context,
						error_message: scrubString(error.message),
						tier: currentTier,
						analysis_id: analysis.id,
					});
				},
			}
		: undefined;

	// Bounded compatibility adapter for older results components.
	// NarrativeClarity envelope is the primary route contract;
	// `displayAnalysis` exists only until those components migrate.
	const displayAnalysis = analysis?.legacyAnalysis ?? null;
	const hasDisplayableAnalysis = !!analysis?.result;

	// Staleness indicator: show expiry warning when TTL is within 6 hours.
	// ttlExpiresAt is only present on DB-fetched results (not React Query cache hits),
	// so this naturally self-filters to shared links and page refreshes.
	const stalenessLabel = useMemo(() => {
		if (!analysis?.ttlExpiresAt) return null;

		const hoursRemaining =
			(new Date(analysis.ttlExpiresAt).getTime() - Date.now()) /
			(1000 * 60 * 60);

		// Only show within the 6-hour warning window
		if (hoursRemaining > 6 || hoursRemaining <= 0) return null;

		if (hoursRemaining > 1) {
			return `Expires in ~${Math.ceil(hoursRemaining)}h`;
		}
		return `Expires in ~${Math.ceil(hoursRemaining * 60)}min`;
	}, [analysis?.ttlExpiresAt]);

	/**
	 * Navigate to home for new analysis
	 */
	const handleNewAnalysis = () => {
		captureEvent("new_analysis_from_results", {
			tier: currentTier,
			source: "results_page",
		});
		router.push({
			pathname: "/(tabs)",
			params: { resetForm: "true" },
		});
	};

	/**
	 * Navigate to subscription
	 */
	const handleUpgrade = () => {
		captureEvent("upgrade_clicked_from_results", {
			current_tier: currentTier,
			source: "results_page",
		});
		router.push("/(tabs)/subscription");
	};

	/**
	 * Navigate to subscription from feature preview (DD-157)
	 * Passes source=feature_preview for A/B test attribution
	 */
	const handleFeaturePreviewUpgrade = () => {
		captureEvent("upgrade_clicked_from_results", {
			current_tier: currentTier,
			source: "feature_preview",
		});
		router.push({
			pathname: "/(tabs)/subscription",
			params: { source: "feature_preview" },
		});
	};

	// Handle loading state (analysis data OR entitlements for authenticated users)
	// Wait for entitlements to resolve so we don't flash the wrong tier view.
	// Safety: only block for entitlements while they're actively loading.
	// If RPC fails, isEntitlementsLoading becomes false and tier falls back to
	// DEFAULT_ENTITLEMENTS ('anonymous') — we render as free rather than spinning forever.
	if (isLoading || (accessToken && isEntitlementsLoading)) {
		return (
			<View style={styles.container}>
				<LinearGradient
					colors={["#0a0012", "#1a0a2e", "#0d1117"]}
					style={StyleSheet.absoluteFill}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
				/>
				<View style={styles.loadingContainer}>
					<ActivityIndicator size="large" color="#ff00ff" />
					<TierBody size="base" style={styles.loadingText}>
						{t("home.modern.loading_results", {
							defaultValue: "Loading results...",
						})}
					</TierBody>
				</View>
			</View>
		);
	}

	// Handle error or not found
	if (error || !analysis) {
		return (
			<View style={styles.container}>
				<LinearGradient
					colors={["#0a0012", "#1a0a2e", "#0d1117"]}
					style={StyleSheet.absoluteFill}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
				/>
				<View style={styles.errorContainer}>
					<TierBody size="lg" style={styles.errorTitle}>
						{t("home.errors.results_not_found", {
							defaultValue: "Results Not Found",
						})}
					</TierBody>
					<TierBody size="base" style={styles.errorMessage}>
						{error?.message ||
							t("home.errors.results_not_found_message", {
								defaultValue:
									"The analysis you're looking for doesn't exist or has been deleted.",
							})}
					</TierBody>
					<Animated.View
						entering={FadeIn.delay(300)}
						style={styles.errorAction}
					>
						<Pressable onPress={handleNewAnalysis}>
							<TierBody size="base" style={styles.backLink}>
								{t("home.modern.back_to_home", {
									defaultValue: "← Back to Home",
								})}
							</TierBody>
						</Pressable>
					</Animated.View>
				</View>
			</View>
		);
	}

	return (
		<ErrorBoundary componentName="ResultsScreen">
			<View style={styles.container}>
				{/* Y2K Gradient Background */}
				<LinearGradient
					colors={["#0a0012", "#1a0a2e", "#0d1117"]}
					style={StyleSheet.absoluteFill}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
				/>

				{/* Offline Banner */}
				<OfflineBanner />

				<Animated.View
					entering={SlideInUp.springify().damping(15)}
					style={styles.resultsContainer}
				>
					<ScrollView
						style={styles.flex}
						contentContainerStyle={[
							styles.resultsContent,
							{ paddingBottom: bottomPadding },
						]}
					>
						{/* Crisis Resources Banner */}
						{analysis.safety?.showResources && (
							<CrisisBanner
								crisisDetected={analysis.safety.crisisDetected}
								severity={analysis.safety.severity}
								moderationEventId={analysis.safety.moderationEventId}
								screen="results"
								testID="crisis-banner-results"
							/>
						)}

						{/* Tier-based conditional rendering */}
						{!hasDisplayableAnalysis ? (
							<View style={styles.errorContainer}>
								<TierBody size="lg" style={styles.errorTitle}>
									{analysis.safety?.crisisDetected
										? "Safety resources available"
										: "Analysis unavailable"}
								</TierBody>
								<TierBody size="base" style={styles.errorMessage}>
									{analysis.result?.summary ||
										analysis.legacyAnalysis.summary ||
										"This analysis does not include a displayable scored result."}
								</TierBody>
							</View>
						) : resultsPolicy.renderVariant === "preview" ? (
							<>
								{/* L1 Tiers: ModernResultsPreview */}
								<ModernResultsPreview
									analysis={displayAnalysis!}
									message={analysis.request.message || analysis.message || ""}
									tier={displayTier}
									onNewAnalysis={handleNewAnalysis}
									onUpgrade={handleUpgrade}
									t={
										t as (
											key: string,
											options?: Record<string, unknown>,
										) => string
									}
									shareTreatment={resultsPolicy.shareTreatment}
									primaryActionTreatment={resultsPolicy.primaryActionTreatment}
									analyticsSurface={params.token ? "shared_link" : "results_page"}
									moduleOrder={resultsPolicy.moduleOrder}
									showLikelyImpact={resultsPolicy.showLikelyImpact}
									showPrimaryRewriteCard={resultsPolicy.showPrimaryRewriteCard}
								/>

								{/* DD-157: Drama+ conversion blur teaser (A/B tested via feature flag) */}
								{resultsPolicy.showUpgradePrompt && (
									<GatedSection
										requiredTier="drama_plus"
										currentTier={
											displayTier as
												| "anonymous"
												| "free"
												| "drama_plus"
												| "dramax"
										}
										onUpgrade={handleFeaturePreviewUpgrade}
										behavior="blur"
										blurIntensity="light"
										contentType="feature_preview"
										screen="ResultsScreen"
										testID="results-drama-plus-gate"
									>
										<DramaPlusPreview
											onUpgrade={handleFeaturePreviewUpgrade}
											testID="results-drama-plus-preview"
										/>
									</GatedSection>
								)}
							</>
						) : resultsPolicy.renderVariant === "generative" ? (
							// L2/L3 Tiers with SDUI: DDGenerativeResults (json-render pipeline)
							<DDGenerativeResults
								analysis={displayAnalysis!}
								message={analysis.request.message || analysis.message || ""}
								context={analysis.request.context || analysis.context}
								tierLevel={tierLevel}
								tier={displayTier}
								t={
									t as (
										key: string,
										options?: Record<string, unknown>,
									) => string
								}
								onNewAnalysis={handleNewAnalysis}
								onUpgrade={handleUpgrade}
								l3AccordionsEnabled={l3AccordionsEnabled}
								tracking={trackingConfig}
								analyticsSurface={params.token ? "shared_link" : "results_page"}
							/>
						) : (
							// L2/L3 Tiers: ModernResultsFull (static fallback)
							<ModernResultsFull
								analysis={displayAnalysis!}
								message={analysis.request.message || analysis.message || ""}
								context={analysis.request.context || analysis.context}
								tierLevel={tierLevel}
								tier={displayTier}
								onNewAnalysis={handleNewAnalysis}
								onUpgrade={handleUpgrade}
								l3AccordionsEnabled={l3AccordionsEnabled}
								t={
									t as (
										key: string,
										options?: Record<string, unknown>,
									) => string
								}
								tracking={trackingConfig}
								shareTreatment={resultsPolicy.shareTreatment}
								primaryActionTreatment={resultsPolicy.primaryActionTreatment}
								analyticsSurface={params.token ? "shared_link" : "results_page"}
								moduleOrder={resultsPolicy.moduleOrder}
								showLikelyImpact={resultsPolicy.showLikelyImpact}
								showPrimaryRewriteCard={resultsPolicy.showPrimaryRewriteCard}
							/>
						)}

						{/* Staleness indicator: subtle expiry warning for DB-fetched results */}
						{stalenessLabel && (
							<TierBody
								size="sm"
								color="text-gray-500"
								style={styles.stalenessIndicator}
							>
								{stalenessLabel}
							</TierBody>
						)}
					</ScrollView>
				</Animated.View>
			</View>
		</ErrorBoundary>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
	flex: {
		flex: 1,
	},
	resultsContainer: {
		flex: 1,
	},
	resultsContent: {
		padding: 24,
		// Note: paddingBottom is applied inline via useResponsiveScreenPadding()
	},
	loadingContainer: {
		flex: 1,
		alignItems: "center",
		justifyContent: "center",
		gap: 16,
	},
	loadingText: {
		marginTop: 16,
		color: "#9CA3AF",
	},
	errorContainer: {
		flex: 1,
		alignItems: "center",
		justifyContent: "center",
		padding: 24,
		gap: 16,
	},
	errorTitle: {
		marginBottom: 8,
		color: "#EF4444",
	},
	errorMessage: {
		textAlign: "center",
		maxWidth: 400,
		color: "#9CA3AF",
	},
	errorAction: {
		marginTop: 24,
	},
	backLink: {
		textDecorationLine: "underline",
		color: "#A855F7",
	},
	stalenessIndicator: {
		marginTop: 16,
		textAlign: "center",
		opacity: 0.6,
	},
});
